import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Chip } from '@cowoo/shared/ui-web';
import { useNavigate } from 'react-router';
import {
  ApiProblem,
  formatDate,
  formatMoney,
  formatTime,
  newIdempotencyKey,
  qk,
  todayBerlin,
} from '@cowoo/shared';
import { api } from '../../lib/api';
import { useAdminStore } from '../../lib/store';
import { Dialog, Field, Panel, PanelRow, fieldClass } from '../../ui';
import { toast } from '../../ui/toast';
import { memberName, statusOf, tariffOf } from './memberRow';

/**
 * The member detail panel (10-web-admin § 10.2).
 *
 * The actions are gated on capability, not on role: § 2.4 gives staff notes,
 * guest passes and blocking, and reserves the GDPR delete and the data
 * export for an owner. What the role cannot do is not rendered — the server
 * would refuse it anyway, and a button that always fails is worse than none.
 */
export function MemberPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const can = useAdminStore((state) => state.can);
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [dialog, setDialog] = useState<'cancellation' | 'credit' | 'export' | 'guest' | 'delete' | null>(
    null,
  );
  const [contractId, setContractId] = useState('');
  const [receivedAt, setReceivedAt] = useState(localNow);
  const [channel, setChannel] = useState<'email' | 'admin'>('email');
  const [kind, setKind] = useState<'ordinary' | 'extraordinary'>('ordinary');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');

  const creditCents = Math.round(Number(creditAmount.replace(',', '.')) * 100);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestDay, setGuestDay] = useState(todayBerlin());
  const [guestLocation, setGuestLocation] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteEmail, setDeleteEmail] = useState('');

  const done = () => {
    setProblem(null);
    setDialog(null);
    void queryClient.invalidateQueries({ queryKey: qk.admin.area('members') });
  };
  const fail = (error: unknown) => setProblem(error instanceof ApiProblem ? error : null);

  const member = useQuery({
    queryKey: qk.admin.area('members', { id }),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/members/{userId}', {
        params: { path: { userId: id } },
      });
      if (error) throw error;
      return data;
    },
  });

  const addNote = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/members/{userId}/notes', {
        params: { path: { userId: id } },
        body: { note: note.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('members') });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  const cancellation = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/members/{userId}/cancellation', {
        params: { path: { userId: id } },
        // `datetime-local` has no zone; the browser's own offset is the one
        // staff are typing in, so the Date does the conversion.
        body: {
          contractId,
          receivedAt: new Date(receivedAt).toISOString(),
          channel,
          kind,
        },
      });
      if (error) throw error;
    },
    onSuccess: done,
    onError: fail,
  });

  const credit = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/members/{userId}/credit', {
        params: { path: { userId: id }, header: { 'idempotency-key': newIdempotencyKey() } },
        body: { amountCents: creditCents, reason: creditReason.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setCreditAmount('');
      setCreditReason('');
      done();
    },
    onError: fail,
  });

  const exportData = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/members/{userId}/export', {
        params: { path: { userId: id } },
      });
      if (error) throw error;
    },
    onSuccess: done,
    onError: fail,
  });

  /** § 10.2 "Gastpass ausstellen" — free, logged (BR-GUEST-05); the guest gets the QR by mail. */
  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    enabled: dialog === 'guest',
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/locations', {});
      if (error) throw error;
      return data.items;
    },
  });
  const openLocations = (locations.data ?? []).filter((location) => location.status === 'open');
  const guestLocationId = guestLocation || openLocations[0]?.id || '';

  const guest = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/members/{userId}/guest-tickets', {
        params: { path: { userId: id } },
        body: {
          guestName: guestName.trim(),
          guestEmail: guestEmail.trim(),
          day: guestDay,
          locationId: guestLocationId,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setGuestName('');
      setGuestEmail('');
      toast(t('admin.members.guestIssued'), { tone: 'success' });
      done();
    },
    onError: fail,
  });

  /**
   * § 10.4 — anonymise. The server checks the preconditions again
   * (`409 contract_active`); the dialog shows them first so nobody types a
   * reason for something that cannot happen. Success closes the panel: the
   * person it described no longer exists under this name.
   */
  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await api.DELETE('/admin/members/{userId}', {
        params: { path: { userId: id } },
        body: { reason: deleteReason.trim(), confirmEmail: deleteEmail.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast(t('admin.members.deleted'), { tone: 'success' });
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('members') });
      void navigate('/mitglieder');
    },
    onError: fail,
  });

  const block = useMutation({
    mutationFn: async (blocked: boolean) => {
      const path = { path: { userId: id } } as const;
      const { error } = blocked
        ? await api.POST('/admin/members/{userId}/unblock', { params: path })
        : await api.POST('/admin/members/{userId}/block', { params: path, body: { reason: note.trim() } });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('members') });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  const data = member.data;
  if (!data)
    return <Panel label={t('admin.members.panelLabel')} title="—" onClose={onClose} children={null} />;

  const status = statusOf(data);

  return (
    <Panel
      label={t('admin.members.panelLabel')}
      title={memberName(data)}
      subtitle={`${data.email}${data.customerNumber ? ` · ${data.customerNumber}` : ''}`}
      onClose={onClose}
    >
      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

      <div className="flex flex-wrap items-center gap-1.5">
        <Chip tone={status.tone} label={t(`admin.members.status.${status.key}`, status.values ?? {})} />
        <Chip tone="teal" label={tariffOf(data, t)} />
        <Chip tone="neutral" label={data.company ?? t('admin.members.private')} />
        {data.bluetoothActive ? <Chip tone="success" label={t('admin.members.bluetooth')} /> : null}
        <Chip
          tone="neutral"
          label={t(data.newsletter ? 'admin.members.newsletterYes' : 'admin.members.newsletterNo')}
        />
      </div>

      <div>
        <PanelRow label={t('admin.members.since')} value={formatDate(data.createdAt.slice(0, 10))} />
        <PanelRow
          label={t('admin.members.payment')}
          value={data.paymentMethod ?? t('admin.members.noPaymentMethod')}
        />
        <PanelRow label={t('admin.members.credit')} value={formatMoney(data.creditCents)} />
        <PanelRow
          label={t('admin.members.invoices')}
          value={
            <Link to={`/rechnungen?user=${data.id}`}>
              {t('admin.members.invoicesN', { count: data.invoiceCount })}
              {data.openInvoices > 0 ? ` · ${t('admin.members.openN', { count: data.openInvoices })}` : ''}
            </Link>
          }
        />
        <PanelRow
          label={t('admin.members.bookings')}
          value={
            <Link to={`/buchungen?user=${data.id}`}>
              {t('admin.members.bookingsN', { count: data.bookingCount })}
            </Link>
          }
        />
        <PanelRow
          label={t('admin.members.guestTickets')}
          value={t('admin.members.guestTicketsN', { count: data.guestTicketCount })}
        />
        <PanelRow
          label={t('admin.members.cancellation')}
          value={
            data.cancellation
              ? t('admin.members.cancelledOn', {
                  end: data.endsOn ? formatDate(data.endsOn) : '—',
                  received: formatDate(data.cancellation.receivedAt.slice(0, 10)),
                })
              : t('admin.members.noCancellation')
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" variant="secondary" onClick={() => setDialog('guest')}>
          {t('admin.members.issueGuestTicket')}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setDialog('cancellation')}>
          {t('admin.members.recordCancellation')}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setDialog('credit')}>
          {t('admin.members.grantCredit')}
        </Button>
        <Button
          size="sm"
          variant={data.accessBlocked ? 'secondary' : 'danger'}
          disabled={!data.accessBlocked && note.trim().length < 5}
          loading={block.isPending}
          onClick={() => block.mutate(data.accessBlocked)}
        >
          {t(data.accessBlocked ? 'admin.members.unblock' : 'admin.members.block')}
        </Button>
        {/* § 2.4 — the export and the GDPR delete are owner-only. */}
        {can('gdpr') ? (
          <Button
            size="sm"
            variant="secondary"
            loading={exportData.isPending}
            onClick={() => setDialog('export')}
          >
            {t('admin.members.exportData')}
          </Button>
        ) : null}
        {can('gdpr') ? (
          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              setDeleteReason('');
              setDeleteEmail('');
              setDialog('delete');
            }}
          >
            {t('admin.members.delete')}
          </Button>
        ) : null}
      </div>
      <p className="text-adminSmall text-muted">{t('admin.members.blockHint')}</p>

      {dialog === 'cancellation' ? (
        <Dialog
          title={t('admin.members.cancellationTitle')}
          confirmLabel={t('admin.members.recordCancellation')}
          onConfirm={() => cancellation.mutate()}
          onClose={() => setDialog(null)}
          busy={cancellation.isPending}
          disabled={contractId === ''}
        >
          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
          <Field label={t('admin.members.cancellationContract')}>
            <select
              value={contractId}
              onChange={(event) => setContractId(event.target.value)}
              className={fieldClass}
            >
              <option value="">{t('admin.members.cancellationPick')}</option>
              {data.contracts.map((contract) => (
                <option key={contract.id} value={contract.id}>
                  {[contract.number, t(`admin.members.product.${contract.productKey}`)]
                    .filter(Boolean)
                    .join(' · ')}
                </option>
              ))}
            </select>
          </Field>
          <div className="flex gap-2.5">
            <div className="flex-1">
              {/* The legally relevant date, so it is entered rather than
                  assumed: BR-CON-05 ends the contract at the month end of the
                  receipt, not of the day staff got round to typing it in. */}
              <Field label={t('admin.members.cancellationReceived')}>
                <input
                  type="datetime-local"
                  value={receivedAt}
                  onChange={(event) => setReceivedAt(event.target.value)}
                  className={fieldClass}
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field label={t('admin.members.cancellationChannel')}>
                <select
                  value={channel}
                  onChange={(event) => setChannel(event.target.value as 'email' | 'admin')}
                  className={fieldClass}
                >
                  <option value="email">{t('admin.members.channel.email')}</option>
                  <option value="admin">{t('admin.members.channel.admin')}</option>
                </select>
              </Field>
            </div>
          </div>
          <Field label={t('admin.members.cancellationKind')}>
            <select
              value={kind}
              onChange={(event) => setKind(event.target.value as 'ordinary' | 'extraordinary')}
              className={fieldClass}
            >
              <option value="ordinary">{t('admin.members.kind.ordinary')}</option>
              <option value="extraordinary">{t('admin.members.kind.extraordinary')}</option>
            </select>
          </Field>
          <p className="text-adminSmall text-muted">
            {kind === 'extraordinary'
              ? t('admin.members.cancellationExtraordinaryHint')
              : t('admin.members.cancellationEndsOn', { date: endOfMonthLabel(receivedAt) })}
          </p>
        </Dialog>
      ) : null}

      {dialog === 'credit' ? (
        <Dialog
          title={t('admin.members.grantCredit')}
          confirmLabel={t('admin.members.grantCredit')}
          onConfirm={() => credit.mutate()}
          onClose={() => setDialog(null)}
          busy={credit.isPending}
          disabled={!(creditCents > 0) || creditReason.trim().length < 5}
        >
          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
          <div className="flex gap-2.5">
            <div className="flex-1">
              <Field label={t('admin.members.creditAmount')}>
                <input
                  value={creditAmount}
                  onChange={(event) => setCreditAmount(event.target.value)}
                  inputMode="decimal"
                  className={fieldClass}
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field label={t('admin.members.creditReason')} hint={t('admin.reasonHint')}>
                <input
                  value={creditReason}
                  onChange={(event) => setCreditReason(event.target.value)}
                  placeholder={t('admin.reasonPlaceholder')}
                  className={fieldClass}
                />
              </Field>
            </div>
          </div>
          <p className="text-adminSmall text-muted">{t('admin.members.creditHint')}</p>
        </Dialog>
      ) : null}

      {dialog === 'guest' ? (
        <Dialog
          title={t('admin.members.guestTitle')}
          confirmLabel={t('admin.members.issueGuestTicket')}
          onConfirm={() => guest.mutate()}
          onClose={() => setDialog(null)}
          busy={guest.isPending}
          disabled={
            guestName.trim().length === 0 || !/.+@.+\..+/.test(guestEmail.trim()) || guestLocationId === ''
          }
        >
          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
          <div className="flex gap-2.5">
            <div className="flex-1">
              <Field label={t('admin.members.guestName')}>
                <input
                  value={guestName}
                  onChange={(event) => setGuestName(event.target.value)}
                  className={fieldClass}
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field label={t('admin.members.guestEmail')}>
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(event) => setGuestEmail(event.target.value)}
                  className={fieldClass}
                />
              </Field>
            </div>
          </div>
          <div className="flex gap-2.5">
            <div className="flex-1">
              <Field label={t('admin.members.guestDay')}>
                <input
                  type="date"
                  value={guestDay}
                  min={todayBerlin()}
                  onChange={(event) => setGuestDay(event.target.value)}
                  className={fieldClass}
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field label={t('admin.members.guestLocation')}>
                <select
                  value={guestLocationId}
                  onChange={(event) => setGuestLocation(event.target.value)}
                  className={fieldClass}
                >
                  {openLocations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>
          <p className="text-adminSmall text-muted">{t('admin.members.guestHint')}</p>
        </Dialog>
      ) : null}

      {dialog === 'delete' ? (
        <DeleteDialog
          email={data.email}
          running={data.contracts.some(
            (contract) => contract.status === 'active' || contract.status === 'cancelled',
          )}
          openInvoices={data.openInvoices}
          creditCents={data.creditCents}
          reason={deleteReason}
          onReason={setDeleteReason}
          typed={deleteEmail}
          onTyped={setDeleteEmail}
          problem={problem}
          busy={remove.isPending}
          onConfirm={() => remove.mutate()}
          onClose={() => {
            setProblem(null);
            setDialog(null);
          }}
        />
      ) : null}

      {dialog === 'export' ? (
        <Dialog
          title={t('admin.members.exportData')}
          intro={t('admin.members.exportHint', { email: data.email })}
          confirmLabel={t('admin.members.exportData')}
          onConfirm={() => exportData.mutate()}
          onClose={() => setDialog(null)}
          busy={exportData.isPending}
        />
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-1.5">
        <p className="text-label text-muted uppercase">{t('admin.members.notes')}</p>
        {data.notes.length === 0 ? (
          <p className="text-adminSmall text-muted">{t('admin.members.noNotes')}</p>
        ) : (
          <ul className="text-adminSmall text-muted flex flex-col gap-1">
            {data.notes.map((entry, index) => (
              <li key={`${entry.at}-${index}`}>
                {formatDate(entry.at)} {formatTime(entry.at)}
                {entry.by ? ` · ${entry.by}` : ''} — {entry.text}
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2 pt-1">
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t('admin.members.addNote')}
            className="border-line-strong rounded-input text-adminBody h-10 flex-1 border px-3"
          />
          <Button
            size="sm"
            disabled={note.trim().length === 0}
            loading={addNote.isPending}
            onClick={() => addNote.mutate()}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in the browser's own zone. */
function localNow(): string {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

/**
 * The end date the receipt implies (BR-CON-05), shown before it is committed.
 *
 * Computed here only to show it: the server decides, and it decides from the
 * same rule. Staff need to see what date they are about to set, because a
 * receipt typed one day late moves it by a whole month.
 */
function endOfMonthLabel(localValue: string): string {
  const received = new Date(localValue);
  if (Number.isNaN(received.getTime())) return '—';
  const end = new Date(received.getFullYear(), received.getMonth() + 1, 0);
  return end.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * § 10.4 — the three preconditions as a checklist, then the reason and the
 * e-mail typed back. The button stays disabled while any line is red, so a
 * `409 contract_active` is the exception rather than the way the rule is
 * learnt.
 */
function DeleteDialog({
  email,
  running,
  openInvoices,
  creditCents,
  reason,
  onReason,
  typed,
  onTyped,
  problem,
  busy,
  onConfirm,
  onClose,
}: {
  email: string;
  running: boolean;
  openInvoices: number;
  creditCents: number;
  reason: string;
  onReason: (value: string) => void;
  typed: string;
  onTyped: (value: string) => void;
  problem: ApiProblem | null;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const checks: Array<{ ok: boolean; label: string }> = [
    {
      ok: !running,
      label: running ? t('admin.members.deleteCheckContractFail') : t('admin.members.deleteCheckContract'),
    },
    {
      ok: openInvoices === 0,
      label:
        openInvoices === 0
          ? t('admin.members.deleteCheckOpen')
          : t('admin.members.deleteCheckOpenFail', { count: openInvoices }),
    },
    {
      ok: creditCents === 0,
      label:
        creditCents === 0
          ? t('admin.members.deleteCheckCredit')
          : t('admin.members.deleteCheckCreditFail', { amount: formatMoney(creditCents) }),
    },
  ];
  const allOk = checks.every((check) => check.ok);

  return (
    <Dialog
      title={t('admin.members.deleteTitle')}
      intro={t('admin.members.deleteRule')}
      tone="danger"
      confirmLabel={t('admin.members.deleteSubmit')}
      onConfirm={onConfirm}
      onClose={onClose}
      busy={busy}
      disabled={!allOk || reason.trim().length < 5 || typed.trim().toLowerCase() !== email.toLowerCase()}
    >
      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
      <ul className="flex flex-col gap-1">
        {checks.map((check) => (
          <li
            key={check.label}
            className={`text-adminBody flex items-center gap-2 ${check.ok ? 'text-success-text' : 'text-danger-text'}`}
          >
            <span aria-hidden>{check.ok ? '✓' : '✕'}</span>
            <span>{check.label}</span>
          </li>
        ))}
      </ul>
      <Field label={t('admin.members.creditReason')} hint={t('admin.reasonHint')}>
        <input
          value={reason}
          onChange={(event) => onReason(event.target.value)}
          placeholder={t('admin.reasonPlaceholder')}
          className={fieldClass}
        />
      </Field>
      <Field label={t('admin.members.deleteConfirmEmail')}>
        <input
          type="email"
          value={typed}
          onChange={(event) => onTyped(event.target.value)}
          placeholder={email}
          autoComplete="off"
          className={fieldClass}
        />
      </Field>
    </Dialog>
  );
}
